import { isBrowser, globalVar } from 'app/infrastructure/universal';
import { AppSetting } from 'dependencies/appsettings';
import { SigninRequestSettings, UserState } from './types';
import { logoutRoute } from './constants';
import { UserManager, UserManagerSettings } from 'oidc-client-ts';
import { debug } from 'app/infrastructure/log';

let userManager: UserManager;

// prevent paralell requests triggering multiple redirects to auth provider
let redirectStarted: boolean;
export const isRedirectStarted = () => redirectStarted;

// Shared across windows/tabs (unlike sessionStorage) so a new window reuses the provider
// the user actually signed in with, instead of falling back to an unrelated SSO session.
const PROVIDER_STORAGE_KEY = 'SL.authProvider';

function storeProvider(providerId: string | undefined) {
  if (!isBrowser || !providerId) {
    return;
  }
  globalVar('localStorage').setItem(PROVIDER_STORAGE_KEY, providerId);
}

function getStoredProvider(): string | undefined {
  return isBrowser ? globalVar('localStorage').getItem(PROVIDER_STORAGE_KEY) || undefined : undefined;
}

function clearStoredProvider() {
  if (isBrowser) {
    globalVar('localStorage').removeItem(PROVIDER_STORAGE_KEY);
  }
}

const logoutPath = (applicationUrl: string) => {
  return (applicationUrl.lastIndexOf('/') === applicationUrl.length - 1
    ? applicationUrl.slice(0, -1) : applicationUrl)
    + logoutRoute;
};

function createAuthCodeSettings(): UserManagerSettings {
  return {
    authority: AppSetting.OidcServiceUrl,
    client_id: AppSetting.OidcClientId,
    client_secret: AppSetting.OidcClientSecret,
    redirect_uri: AppSetting.ApplicationUrl,
    post_logout_redirect_uri: logoutPath(AppSetting.ApplicationUrl),
    scope: AppSetting.OidcScope,
    monitorSession: false,
    automaticSilentRenew: false,
    filterProtocolClaims: true,
    loadUserInfo: !AppSetting.OidcDontLoadUserInfo
  };
}

function createUserManager(): UserManager {
  const settings = createAuthCodeSettings();
  return new UserManager(settings);
}

export function removeOidcScope(scope: string, scopeToRemove: string): string {
  if (!scopeToRemove) {
    return scope;
  }

  return scope.split(/\s+/).filter(value => value && value !== scopeToRemove).join(' ');
}

// Unknown (undefined) discovery metadata is treated as supported, since some
// providers omit scopes_supported entirely; we must not strip scopes on a guess.
export function isScopeSupported(scope: string, supportedScopes: string[] | undefined): boolean {
  return !supportedScopes || supportedScopes.includes(scope);
}

// The auth server (SLAS) shows its own error page for invalid_scope instead of redirecting back,
// so an unsupported AI Chat scope must be filtered out before signinRedirect, not handled afterwards.
// Only the configured AI Chat scope is checked here: other scopes (e.g. API resource scopes) are
// often intentionally hidden from scopes_supported and must never be stripped.
async function resolveSigninScope(): Promise<string> {
  const aiChatScope = AppSetting.AiChat?.scope?.trim();
  if (!aiChatScope) {
    return AppSetting.OidcScope;
  }

  try {
    const metadata = await userManager.metadataService.getMetadata();
    if (!isScopeSupported(aiChatScope, metadata.scopes_supported)) {
      debug('AI Chat scope is not supported by the OIDC provider. Requesting without it.');
      return removeOidcScope(AppSetting.OidcScope, aiChatScope);
    }
  } catch (err) {
    debug('Failed to load OIDC discovery metadata. Requesting the configured scope as-is.');
  }

  return AppSetting.OidcScope;
}

async function accessExpiringCallback() {
  debug('Access token expiring');
  if (!await renewToken()) {
    debug('Failed silent sign in. Redirecting...');
    reAuthenticate();
  }
}

export function initialize() {
  if (!isBrowser || userManager) {
    return;
  }
  userManager = createUserManager();
  userManager.clearStaleState();
  userManager.events.addAccessTokenExpiring(accessExpiringCallback);
}

export function removeUserManagerEvents() {
  userManager?.events.removeAccessTokenExpiring(accessExpiringCallback);
}

export const routerPath = (href: string, hostingPath: string) => {
  if (href === undefined || href === null) {
    return '/';
  }

  const hostPosition = href.indexOf('://') !== -1 ? href.indexOf('://') + 3 : 0;
  const locationPosition = href.indexOf('/', hostPosition);
  const locationPath = locationPosition !== -1 ? href.substring(locationPosition) : '/';

  if (hostingPath === undefined || hostingPath === null || hostingPath.length === 0) {
    return locationPath;
  }

  const hostingPathPosition = locationPath.toLowerCase().indexOf(hostingPath.toLowerCase());
  const hostingPathLength = hostingPath.length - (hostingPath.lastIndexOf('/') === hostingPath.length - 1 ? 1 : 0);
  return hostingPathPosition === -1 || locationPath.length === hostingPathPosition + hostingPathLength
    ? '/'
    : locationPath.substring(hostingPathPosition + hostingPathLength);
};

export function reAuthenticate() {
  if (redirectStarted) {
    return;
  }
  redirectStarted = true;
  signin(true, true);
}

async function renewToken(): Promise<boolean> {
  try {
    debug('Silent sign in');
    await userManager.signinSilent();
    return true;
  } catch (err) {
    return false;
  }
}

export async function signin(
  logAsDifferentUser: boolean = false,
  storeCurrentRouterPath: boolean = true,
  scope?: string
) {
  initialize();

  const path = storeCurrentRouterPath
    ? routerPath(globalVar('location').href, AppSetting.HostingPath)
    : '/';

  const settings: SigninRequestSettings = {
    state: { redirectUrl: path }
  };

  const providerId = getStoredProvider() || AppSetting.OidcDefaultProviderId;

  if (logAsDifferentUser) {
    settings.prompt = 'login';
  } else if (providerId) {
    settings.acr_values = 'idp:' + providerId;
  }

  if (scope) {
    settings.scope = scope;
  } else {
    settings.scope = await resolveSigninScope();
  }

  await userManager.signinRedirect(settings);
}

export async function processSigninResponse(url: string) {
  initialize();
  const user = await userManager.signinRedirectCallback(url);
  storeProvider(user?.profile?.idp as string);
  return (user?.state as UserState)?.redirectUrl;
}

export async function signout() {
  initialize();
  clearStoredProvider();
  userManager.signoutRedirect();
  userManager.removeUser();
}

export async function getAccessToken() {
  initialize();
  const user = await userManager?.getUser();
  return user?.access_token;
}

export async function hasToken() {
  return !!getAccessToken();
}

